// =============================================================================
// MeuDriver — Hook: usePackages
// Pacotes do ALUNO: compra (Pix) de pacotes montados por instrutores e uso dos
// créditos para agendar. Gestão dos pacotes pelo instrutor fica em
// useLessonPackages.
//
// Preço: o instrutor define o valor do pacote; o aluno paga esse valor + a
// taxa da plataforma (getStudentPrice). A edge function `purchase-package`
// recalcula no servidor e paga com o saldo da carteira (Pix só da diferença).
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import type { StudentPackageRow } from '@/types/database'

/** Pacote do aluno com o pacote do instrutor e o instrutor embutidos. */
export type MyPackage = StudentPackageRow & {
  instructor_package: {
    id: string
    name: string
    lesson_count: number
    instructor: {
      id: string
      profile: { full_name: string | null; avatar_url: string | null } | null
    } | null
  } | null
}

const MY_PACKAGE_SELECT = `*,
  instructor_package:instructor_packages(
    id, name, lesson_count,
    instructor:instructors!instructor_packages_instructor_id_fkey(
      id,
      profile:profiles!instructors_profile_id_fkey(full_name, avatar_url)
    )
  )`

/** Créditos ainda utilizáveis (ativo, com saldo e dentro da validade). */
export function isUsablePackage(p: StudentPackageRow): boolean {
  return p.status === 'active'
    && p.lessons_remaining > 0
    && new Date(p.expires_at).getTime() > Date.now()
}

export function useStudentPackages() {
  const { user } = useAuth()

  return useQuery<MyPackage[]>({
    queryKey: ['student-packages', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('student_packages')
        .select(MY_PACKAGE_SELECT)
        .eq('student_id', user!.id)
        .not('instructor_package_id', 'is', null)
        .neq('status', 'cancelled')
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as MyPackage[]
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60,
  })
}

/** Pacotes utilizáveis do aluno com um instrutor (o que vence antes primeiro). */
export function useUsablePackagesForInstructor(instructorId: string | undefined) {
  const query = useStudentPackages()
  const usable = (query.data ?? [])
    .filter((p) => p.instructor_package?.instructor?.id === instructorId && isUsablePackage(p))
    .sort((a, b) => a.expires_at.localeCompare(b.expires_at))
  return { ...query, data: usable }
}

export function usePurchasePackage() {
  const qc = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (params: { package_id: string }) => {
      const { data, error } = await supabase.functions.invoke('purchase-package', { body: params })
      if (error) {
        let msg = 'Erro ao processar compra.'
        try {
          const ctx = (error as { context?: Response }).context
          const body = ctx ? await ctx.json() : null
          if (body?.error === 'own_package') msg = 'Você não pode comprar o próprio pacote.'
          else if (body?.error === 'package_not_found') msg = 'Pacote indisponível.'
          else if (body?.message) msg = body.message
        } catch { /* mantém a mensagem genérica */ }
        throw new Error(msg)
      }
      return data as {
        student_package_id: string
        paid_with_wallet?: boolean
        charge_id?: string
        payment_url?: string
      }
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['student-packages', user?.id] })
      qc.invalidateQueries({ queryKey: ['wallet'] })
      if (data.paid_with_wallet || !data.payment_url) {
        toast.success('Pacote comprado com o seu saldo! Já pode agendar as aulas.')
        return
      }
      // Faltou saldo: Pix da diferença (/pagamento/:id); o mp-webhook ativa o pacote.
      window.location.assign(data.payment_url)
    },
    onError: (err: Error) => {
      toast.error(err.message)
    },
  })
}

const BOOK_ERRORS: Record<string, string> = {
  package_no_lessons_left: 'Seu pacote não tem mais aulas disponíveis.',
  package_expired: 'Seu pacote expirou.',
  package_not_active: 'Seu pacote ainda não está ativo (aguardando pagamento).',
  package_not_found: 'Pacote não encontrado.',
  slot_unavailable: 'Esse horário acabou de ser ocupado. Escolha outro.',
  invalid_scheduled_at: 'Escolha um horário com pelo menos 1 hora de antecedência.',
}

export function useBookPackageLesson() {
  const qc = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (params: {
      studentPackageId: string
      scheduledAt: string
      pickupAddress: string | null
      pickupLat: number | null
      pickupLng: number | null
      notes: string | null
    }) => {
      const { data, error } = await supabase.rpc('book_package_lesson', {
        p_student_package_id: params.studentPackageId,
        p_scheduled_at: params.scheduledAt,
        p_pickup_address: params.pickupAddress,
        p_pickup_lat: params.pickupLat,
        p_pickup_lng: params.pickupLng,
        p_notes: params.notes,
      })
      if (error) {
        const key = Object.keys(BOOK_ERRORS).find((k) => error.message.includes(k))
        throw new Error(key ? BOOK_ERRORS[key] : 'Não foi possível agendar a aula.')
      }
      return data as string
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['student-packages', user?.id] })
      qc.invalidateQueries({ queryKey: ['bookings'] })
    },
  })
}
